import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  padding: 40px 0;
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
  color: white;
  border-radius: 12px;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin: 0 0 15px 0;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 0;
  opacity: 0.9;
`;

const ContentSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 1.8rem;
  margin: 0 0 20px 0;
  border-bottom: 3px solid #1a365d;
  padding-bottom: 10px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin: 15px 0;
  padding: 15px;
  background: #ebf8ff;
  border-radius: 8px;
  border-left: 4px solid #1a365d;
`;

const BenefitIcon = styled.span`
  font-size: 1.5rem;
  margin-right: 15px;
  color: #1a365d;
`;

const DangerBox = styled.div`
  background: #fed7d7;
  border: 2px solid #fc8181;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const DangerTitle = styled.h4`
  color: #c53030;
  margin: 0 0 15px 0;
  font-size: 1.2rem;
`;

const DangerText = styled.p`
  color: #c53030;
  margin: 10px 0;
  line-height: 1.5;
`;

const SafetyBox = styled.div`
  background: #c6f6d5;
  border: 2px solid #68d391;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const SafetyTitle = styled.h4`
  color: #22543d;
  margin: 0 0 15px 0;
  font-size: 1.2rem;
`;

const SafetyText = styled.p`
  color: #22543d;
  margin: 10px 0;
  line-height: 1.5;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
  color: white;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  margin: 30px 0;
`;

const CTATitle = styled.h3`
  font-size: 1.8rem;
  margin: 0 0 15px 0;
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  margin: 0 0 25px 0;
  opacity: 0.9;
`;

const AgentLiability = () => {
  return (
    <>
      <Helmet>
        <title>ShowingBooker: Agent Liability Protection - Not a Property Manager</title>
        <meta name="description" content="Real estate agents: Avoid liability with professional showing management. You're not a property manager - let ShowingBooker handle tenant notices and compliance." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://showingbooker.com/agent-liability" />
        <meta property="og:title" content="ShowingBooker: Agent Liability Protection" />
        <meta property="og:description" content="Real estate agents: Avoid liability with professional showing management. You're not a property manager - let ShowingBooker handle tenant notices and compliance." />
        <meta property="og:image" content="https://showingbooker.com/ShowingBooker - Logo - Social Media Card.png" />
        <meta property="og:site_name" content="ShowingBooker" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://showingbooker.com/agent-liability" />
        <meta property="twitter:title" content="ShowingBooker: Agent Liability Protection" />
        <meta property="twitter:description" content="Real estate agents: Avoid liability with professional showing management. You're not a property manager - let ShowingBooker handle tenant notices and compliance." />
        <meta property="twitter:image" content="https://showingbooker.com/ShowingBooker - Logo - Social Media Card.png" />
      </Helmet>
      
      <Container>
        <Header>
          <Title>Agent Liability Protection</Title>
          <Subtitle>You're not a property manager - stay out of tenant disputes</Subtitle>
        </Header>

        <ContentSection>
          <SectionTitle>🚫 You're NOT a Property Manager</SectionTitle>
          <DangerBox>
            <DangerTitle>Dangerous Activities Agents Should Avoid:</DangerTitle>
            <DangerText>
              <strong>• Sending Legal Notices:</strong> Property managers require special licensing and training for tenant communications
            </DangerText>
            <DangerText>
              <strong>• Managing Tenant Relations:</strong> Not your job and exposes you to liability you're not equipped to handle
            </DangerText>
            <DangerText>
              <strong>• Compliance Management:</strong> Municipal codes change frequently and violations carry personal liability
            </DangerText>
            <DangerText>
              <strong>• Tenant Dispute Resolution:</strong> Getting involved in landlord-tenant issues is outside your expertise
            </DangerText>
            <DangerText>
              <strong>• Notice Service:</strong> Improper notice can void sales contracts and create legal exposure
            </DangerText>
          </DangerBox>
        </ContentSection>

        <ContentSection>
          <SectionTitle>🎯 Why Agents Use ShowingBooker</SectionTitle>
          <BenefitsList>
            <BenefitItem>
              <BenefitIcon>🛡️</BenefitIcon>
              <div>
                <strong>Liability Protection:</strong> Third-party platform handles all tenant communications, keeping you out of disputes
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>📋</BenefitIcon>
              <div>
                <strong>Professional Distance:</strong> No direct tenant confrontations, complaints, or harassment accusations
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>⚖️</BenefitIcon>
              <div>
                <strong>Compliance Expertise:</strong> ShowingBooker handles legal requirements you're not trained to manage
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>💼</BenefitIcon>
              <div>
                <strong>Focus on Sales:</strong> Spend time selling, not managing tenant relations and notice requirements
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>🕒</BenefitIcon>
              <div>
                <strong>Time Savings:</strong> No more coordinating schedules, sending notices, or managing tenant complaints
              </div>
            </BenefitItem>
          </BenefitsList>
        </ContentSection>

        <ContentSection>
          <SectionTitle>⚠️ Real Agent Liability Risks</SectionTitle>
          <DangerBox>
            <DangerTitle>What Could Go Wrong Without Professional Management:</DangerTitle>
            <DangerText>
              <strong>• Tenant Harassment Claims:</strong> Tenants can file complaints with your real estate board and city agencies
            </DangerText>
            <DangerText>
              <strong>• License Issues:</strong> Improper tenant interactions can trigger DRE investigations and license problems
            </DangerText>
            <DangerText>
              <strong>• Legal Exposure:</strong> Inadequate notices can void sales contracts and create buyer/seller disputes
            </DangerText>
            <DangerText>
              <strong>• City Violations:</strong> Municipal code violations can result in personal fines and legal action
            </DangerText>
            <DangerText>
              <strong>• Professional Reputation:</strong> Tenant complaints damage your reputation and referral business
            </DangerText>
            <DangerText>
              <strong>• E&O Insurance Claims:</strong> Errors in notice service can trigger expensive insurance claims
            </DangerText>
          </DangerBox>
        </ContentSection>

        <ContentSection>
          <SectionTitle>✅ ShowingBooker Agent Protection</SectionTitle>
          <SafetyBox>
            <SafetyTitle>How ShowingBooker Protects Real Estate Agents:</SafetyTitle>
            <SafetyText>
              <strong>• Third-Party Buffer:</strong> ShowingBooker is the "bad guy" enforcing rules, not you
            </SafetyText>
            <SafetyText>
              <strong>• Professional Notice Service:</strong> Automated legal compliance without your involvement
            </SafetyText>
            <SafetyText>
              <strong>• No Direct Tenant Contact:</strong> All communications go through the platform, protecting your reputation
            </SafetyText>
            <SafetyText>
              <strong>• Documentation Protection:</strong> Complete paper trail proves you followed proper procedures
            </SafetyText>
            <SafetyText>
              <strong>• Tenant Satisfaction:</strong> Professional process reduces complaints and improves your reputation
            </SafetyText>
            <SafetyText>
              <strong>• Legal Compliance:</strong> Expert handling of municipal codes and tenant rights requirements
            </SafetyText>
          </SafetyBox>
        </ContentSection>

        <ContentSection>
          <SectionTitle>💰 Agent Cost-Benefit Analysis</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div style={{ background: '#fed7d7', padding: '20px', borderRadius: '8px', border: '2px solid #fc8181' }}>
              <h4 style={{ color: '#c53030', margin: '0 0 15px 0' }}>DIY Showing Management:</h4>
              <ul style={{ color: '#c53030', margin: '0', paddingLeft: '20px' }}>
                <li>Your time coordinating schedules</li>
                <li>Your liability for notice mistakes</li>
                <li>Direct tenant confrontations</li>
                <li>Potential DRE issues</li>
                <li>E&O insurance claims risk</li>
                <li>Reputation damage from complaints</li>
              </ul>
            </div>
            <div style={{ background: '#c6f6d5', padding: '20px', borderRadius: '8px', border: '2px solid #68d391' }}>
              <h4 style={{ color: '#22543d', margin: '0 0 15px 0' }}>ShowingBooker Professional Service:</h4>
              <ul style={{ color: '#22543d', margin: '0', paddingLeft: '20px' }}>
                <li>FREE for listing agents</li>
                <li>Professional liability protection</li>
                <li>Zero tenant management hassles</li>
                <li>Automated legal compliance</li>
                <li>Enhanced professional reputation</li>
                <li>Focus on selling, not managing</li>
              </ul>
            </div>
          </div>
        </ContentSection>

        <ContentSection>
          <SectionTitle>🏆 Perfect for Professional Agents</SectionTitle>
          <div style={{ lineHeight: '1.6' }}>
            <p><strong>High-Volume Agents:</strong> Systematic approach handles multiple properties without overwhelming your schedule</p>
            <p><strong>Luxury Market Agents:</strong> Professional presentation maintains your premium brand image</p>
            <p><strong>Team Leaders:</strong> Standardized process protects your entire team from liability issues</p>
            <p><strong>New Agents:</strong> Professional systems help you avoid beginner mistakes that could damage your career</p>
            <p><strong>Experienced Agents:</strong> Focus on what you do best - selling real estate, not managing tenants</p>
          </div>
        </ContentSection>

        <CTASection>
          <CTATitle>Protect Your License & Reputation</CTATitle>
          <CTAText>
            You didn't get your real estate license to become a property manager. 
            Let ShowingBooker handle tenant relations while you focus on sales.
          </CTAText>
          <CTAText>
            <strong>FREE for listing agents. Maximum protection for your business.</strong>
          </CTAText>
          <p style={{ marginTop: '30px', fontSize: '1.1rem', fontWeight: '600' }}>
            🌐 Protect your real estate career at showingbooker.com
          </p>
        </CTASection>
      </Container>
    </>
  );
};

export default AgentLiability;